import { useTheme } from '@mui/material/styles';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { httpClient } from '../../../common/httpclient';
import PageScaffold from '../../../component/pageScaffold/PageScaffold';
import DefaultTable from '../../../component/table/DefaultTable';
import DialogForm from '../../../component/dialog/DialogForm';
import Dropdown from '../../../component/dropdown/Dropdown';
import { standaloneFieldSx } from '../../../common/fieldStyles';
import { planDisplayName } from '../../../common/planDisplayName';
import FilterListIcon from '@mui/icons-material/FilterList';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Button from '@mui/material/Button';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import Chip from '@mui/material/Chip';
import InputAdornment from '@mui/material/InputAdornment';
import TextField from '@mui/material/TextField';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import SearchIcon from '@mui/icons-material/Search';
import BusinessIcon from '@mui/icons-material/Business';
import CardMembershipIcon from '@mui/icons-material/CardMembership';
import AssessmentIcon from '@mui/icons-material/Assessment';
import EditIcon from '@mui/icons-material/Edit';

export default function SubscriptionsList() {
    const theme = useTheme();
    const navigate = useNavigate();
    const [subscriptions, setSubscriptions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [enterpriseNameFilter, setEnterpriseNameFilter] = useState('');
    const [planNameFilter, setPlanNameFilter] = useState('');
    const [filteredData, setFilteredData] = useState([]);
    const [openChangePlanDialog, setOpenChangePlanDialog] = useState(false);
    const [selectedSubscription, setSelectedSubscription] = useState(null);
    const [plans, setPlans] = useState([]);
    const [selectedPlanId, setSelectedPlanId] = useState('');
    const [loadingPlans, setLoadingPlans] = useState(false);
    const [loadingChangePlan, setLoadingChangePlan] = useState(false);
    const [openSnackbar, setOpenSnackbar] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const [snackbarSeverity, setSnackbarSeverity] = useState('success');

    const fetchSubscriptions = async () => {
        try {
            setLoading(true);
            const response = await httpClient.get('/admin/subscription');
            
            if (response.status === 200 && response.data) {
                const data = Array.isArray(response.data) ? response.data : [];
                setSubscriptions(data);
                applyFilters(data, enterpriseNameFilter, planNameFilter);
            } else {
                setSubscriptions([]);
                setFilteredData([]);
            }
        } catch (error) {
            console.error('Error fetching subscriptions:', error);
            setSubscriptions([]);
            setFilteredData([]);
        } finally {
            setLoading(false);
        }
    };

    const applyFilters = (data, enterpriseFilter, planFilter) => {
        let filtered = [...data];
        
        if (enterpriseFilter) {
            const normalizedFilter = enterpriseFilter.toLowerCase().trim();
            filtered = filtered.filter(item => 
                item.enterprise_name?.toLowerCase().includes(normalizedFilter)
            );
        }
        
        if (planFilter) {
            const normalizedFilter = planFilter.toLowerCase().trim();
            filtered = filtered.filter(item => 
                item.plan_name?.toLowerCase().includes(normalizedFilter) ||
                planDisplayName(item.plan_name)?.toLowerCase().includes(normalizedFilter) ||
                planDisplayName(item.plan_name)?.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '').includes(normalizedFilter)
            );
        }
        
        setFilteredData(filtered);
    };

    useEffect(() => {
        fetchSubscriptions();
    }, []);

    useEffect(() => {
        applyFilters(subscriptions, enterpriseNameFilter, planNameFilter);
    }, [enterpriseNameFilter, planNameFilter, subscriptions]);

    const handleShowSuccessSnackbar = (message = 'Operação realizada com sucesso!') => {
        setOpenSnackbar(true);
        setSnackbarMessage(message);
        setSnackbarSeverity('success');
    };

    const handleShowErrorSnackbar = (message = 'Erro ao realizar operação') => {
        setOpenSnackbar(true);
        setSnackbarMessage(message);
        setSnackbarSeverity('error');
    };

    const handleCloseSnackbar = () => {
        setOpenSnackbar(false);
    };

    const fetchPlans = async () => {
        try {
            setLoadingPlans(true);
            const response = await httpClient.get('/admin/plans');
            if (response.status === 200 && response.data) {
                const data = Array.isArray(response.data) ? response.data : [];
                setPlans(data);
            } else {
                setPlans([]);
            }
        } catch (error) {
            console.error('Error fetching plans:', error);
            setPlans([]);
            handleShowErrorSnackbar('Erro ao carregar planos');
        } finally {
            setLoadingPlans(false);
        }
    };

    const handleOpenChangePlanDialog = (subscription) => {
        setSelectedSubscription(subscription);
        setSelectedPlanId(subscription.plan_id || '');
        setOpenChangePlanDialog(true);
        fetchPlans();
    };

    const handleCloseChangePlanDialog = () => {
        setOpenChangePlanDialog(false);
        setSelectedSubscription(null);
        setSelectedPlanId('');
    };

    const handleChangePlan = async () => {
        if (!selectedPlanId) {
            handleShowErrorSnackbar('Selecione um plano');
            return;
        }

        if (!selectedSubscription) {
            handleShowErrorSnackbar('Assinatura não selecionada');
            return;
        }

        try {
            setLoadingChangePlan(true);
            const response = await httpClient.put(
                `/admin/subscription/${selectedSubscription.enterprise_id}/plan`,
                { plan_id: selectedPlanId }
            );

            if (response.status === 200 || response.status === 204) {
                handleShowSuccessSnackbar('Plano alterado com sucesso!');
                handleCloseChangePlanDialog();
                fetchSubscriptions();
            } else {
                const errorMessage = response.data?.message || response.data?.id || 'Erro ao alterar plano';
                handleShowErrorSnackbar(errorMessage);
            }
        } catch (error) {
            console.error('Error changing plan:', error);
            const errorMessage = error.response?.data?.message || error.response?.data?.id || 'Erro ao alterar plano';
            handleShowErrorSnackbar(errorMessage);
        } finally {
            setLoadingChangePlan(false);
        }
    };

    const handleEnterpriseNameChange = (value) => {
        setEnterpriseNameFilter(value);
    };

    const handlePlanNameChange = (value) => {
        setPlanNameFilter(value);
    };

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        try {
            const date = new Date(dateString);
            return date.toLocaleDateString('pt-BR', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
            });
        } catch (error) {
            return dateString;
        }
    };

    const columns = [
        {
            name: 'Empresa',
            path: 'enterprise_name'
        },
        {
            name: 'Plano',
            path: 'plan_name',
            render: (row) => planDisplayName(row.plan_name)
        },
        {
            name: 'Data de Criação',
            path: 'date_created',
            render: (row) => {
                return (
                    <Typography variant="body2">
                        {formatDate(row.date_created)}
                    </Typography>
                );
            }
        }
    ];

    const actions = [
        {
            icon: <EditIcon />,
            props: {
                onClick: (row) => {
                    handleOpenChangePlanDialog(row);
                },
                tooltip: 'Alterar Plano',
                style: { cursor: 'pointer' }
            }
        },
        {
            icon: <AssessmentIcon />,
            props: {
                onClick: (row) => {
                    navigate(`/admin/subscriptions/resources?enterpriseId=${row.enterprise_id}`);
                },
                tooltip: 'Ver Recursos',
                style: { cursor: 'pointer' }
            }
        }
    ];

    return (
        <PageScaffold links={[]} titlePageTarget={'Assinaturas'}>
            {/* Seção de Filtros Moderna */}
            <Card
                sx={{
                    m: 0,
                    borderRadius: 3,
                    boxShadow: theme.palette.mode === 'dark'
                        ? '0 4px 20px rgba(0,0,0,0.3)'
                        : '0 4px 20px rgba(0,0,0,0.08)',
                    background: theme.palette.mode === 'dark'
                        ? 'linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)'
                        : 'linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%)',
                    border: theme.palette.mode === 'dark'
                        ? '1px solid rgba(255,255,255,0.1)'
                        : '1px solid rgba(0,0,0,0.05)'
                }}
            >
                <CardContent sx={{ p: 3 }}>
                    {/* Header dos Filtros */}
                    <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        mb: 3,
                        gap: 1
                    }}>
                        <FilterListIcon sx={{ color: 'primary.main', fontSize: 28 }} />
                        <Typography variant="h6" sx={{
                            fontWeight: 600,
                            color: 'text.primary',
                            fontSize: '1.25rem'
                        }}>
                            Filtros de Assinaturas
                        </Typography>
                    </Box>

                    <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                        {/* Campo de Busca por Nome da Empresa */}
                        <Box sx={{ flex: '1 1 300px', minWidth: { xs: 0, sm: '300px' } }}>
                            <TextField
                                id="enterprise-name-filter-input"
                                placeholder="Buscar por nome da empresa..."
                                value={enterpriseNameFilter}
                                onChange={(e) => handleEnterpriseNameChange(e.target.value)}
                                variant="outlined"
                                size="small"
                                fullWidth
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <BusinessIcon sx={{ color: 'primary.main' }} />
                                        </InputAdornment>
                                    ),
                                }}
                                sx={{
                                    borderRadius: 2,
                                    backgroundColor: theme.palette.mode === 'dark'
                                        ? 'rgba(255,255,255,0.05)'
                                        : 'transparent',
                                    '& .MuiOutlinedInput-root': {
                                        '&:hover fieldset': {
                                            borderColor: 'primary.main',
                                        },
                                        '&.Mui-focused fieldset': {
                                            borderColor: 'primary.main',
                                        },
                                    },
                                    '& .MuiOutlinedInput-notchedOutline': {
                                        borderColor: theme.palette.mode === 'dark'
                                            ? 'rgba(255,255,255,0.2)'
                                            : 'rgba(0,0,0,0.1)',
                                    },
                                    '& .MuiInputBase-input': {
                                        color: theme.palette.text.primary,
                                    },
                                }}
                            />
                        </Box>

                        {/* Campo de Busca por Nome do Plano */}
                        <Box sx={{ flex: '1 1 300px', minWidth: { xs: 0, sm: '300px' } }}>
                            <TextField
                                id="plan-name-filter-input"
                                placeholder="Buscar por nome do plano..."
                                value={planNameFilter}
                                onChange={(e) => handlePlanNameChange(e.target.value)}
                                variant="outlined"
                                size="small"
                                fullWidth
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <CardMembershipIcon sx={{ color: 'primary.main' }} />
                                        </InputAdornment>
                                    ),
                                }}
                                sx={{
                                    borderRadius: 2,
                                    backgroundColor: theme.palette.mode === 'dark'
                                        ? 'rgba(255,255,255,0.05)'
                                        : 'transparent',
                                    '& .MuiOutlinedInput-root': {
                                        '&:hover fieldset': {
                                            borderColor: 'primary.main',
                                        },
                                        '&.Mui-focused fieldset': {
                                            borderColor: 'primary.main',
                                        },
                                    },
                                    '& .MuiOutlinedInput-notchedOutline': {
                                        borderColor: theme.palette.mode === 'dark'
                                            ? 'rgba(255,255,255,0.2)'
                                            : 'rgba(0,0,0,0.1)',
                                    },
                                    '& .MuiInputBase-input': {
                                        color: theme.palette.text.primary,
                                    },
                                }}
                            />
                        </Box>
                    </Box>

                    {/* Ações Rápidas */}
                    <Box sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        mt: 3,
                        pt: 2,
                        borderTop: theme.palette.mode === 'dark'
                            ? '1px solid rgba(255,255,255,0.08)'
                            : '1px solid rgba(0,0,0,0.08)'
                    }}>
                        <Stack direction="row" spacing={1} flexWrap="wrap" gap={1}>
                            {enterpriseNameFilter && (
                                <Chip
                                    label={`Empresa: "${enterpriseNameFilter}"`}
                                    onDelete={() => handleEnterpriseNameChange('')}
                                    color="primary"
                                    variant="outlined"
                                    size="small"
                                    sx={{
                                        borderColor: theme.palette.mode === 'dark'
                                            ? 'rgba(255,255,255,0.2)'
                                            : undefined,
                                        color: theme.palette.mode === 'dark'
                                            ? 'primary.light'
                                            : undefined,
                                    }}
                                />
                            )}
                            {planNameFilter && (
                                <Chip
                                    label={`Plano: "${planNameFilter}"`}
                                    onDelete={() => handlePlanNameChange('')}
                                    color="secondary"
                                    variant="outlined"
                                    size="small"
                                    sx={{
                                        borderColor: theme.palette.mode === 'dark'
                                            ? 'rgba(255,255,255,0.2)'
                                            : undefined,
                                        color: theme.palette.mode === 'dark'
                                            ? 'secondary.light'
                                            : undefined,
                                    }}
                                />
                            )}
                            <Chip
                                label={`${filteredData.length} assinatura(s) encontrada(s)`}
                                color="info"
                                variant="outlined"
                                size="small"
                                sx={{
                                    borderColor: theme.palette.mode === 'dark'
                                        ? 'rgba(255,255,255,0.2)'
                                        : undefined,
                                    color: theme.palette.mode === 'dark'
                                        ? 'info.light'
                                        : undefined,
                                }}
                            />
                        </Stack>
                        <Button
                            variant="contained"
                            startIcon={<AddCircleOutlineIcon />}
                            onClick={() => navigate('/admin/subscriptions/new')}
                            sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 500, px: 2 }}
                        >
                            Novo Tenant
                        </Button>
                    </Box>
                </CardContent>
            </Card>

            <Box sx={{ width: '100%', maxWidth: '100%', overflow: 'hidden', mt: 2 }}>
                <DefaultTable
                    colunms={columns}
                    data={filteredData}
                    iconsActions={actions}
                    loading={loading}
                    sortableFields={['enterprise_name', 'plan_name', 'date_created']}
                    defaultSort={{ field: 'date_created', direction: 'desc' }}
                />
            </Box>

            {/* Dialog para Alterar Plano */}
            <DialogForm
                open={openChangePlanDialog}
                onClose={handleCloseChangePlanDialog}
                onSubmit={handleChangePlan}
                title="Alterar Plano da Assinatura"
                iconType="edit"
                content={
                    selectedSubscription ? (
                        `Altere o plano da assinatura da empresa "${selectedSubscription.enterprise_name}".`
                    ) : (
                        'Selecione um novo plano para a assinatura.'
                    )
                }
                confirmButtonText="Alterar Plano"
                cancelButtonText="Cancelar"
                loading={loadingChangePlan}
                loadingText="Alterando plano..."
            >
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
                    <Dropdown
                        data={plans.filter(plan =>
                            plan.is_visible || plan.id === selectedSubscription?.plan_id
                        )}
                        selectedItem={selectedPlanId}
                        handleChange={(value) => setSelectedPlanId(value)}
                        label="Selecione o Plano"
                        labelVariant="static-micro"
                        size="medium"
                        loading={loadingPlans}
                        getOptionLabel={(option) => planDisplayName(option?.name) || ''}
                        getOptionValue={(option) => option?.id || ''}
                        variant="outlined"
                        sx={standaloneFieldSx}
                    />
                    {selectedSubscription && (
                        <Typography variant="body2" sx={{ color: 'text.secondary', mt: 1 }}>
                            Plano atual: <strong>{planDisplayName(selectedSubscription.plan_name)}</strong>
                        </Typography>
                    )}
                </Box>
            </DialogForm>

            {/* Snackbar para mensagens */}
            <Snackbar
                open={openSnackbar}
                autoHideDuration={6000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
                <Alert
                    onClose={handleCloseSnackbar}
                    severity={snackbarSeverity}
                    variant="filled"
                    sx={{ width: '100%' }}
                >
                    {snackbarMessage}
                </Alert>
            </Snackbar>
        </PageScaffold>
    );
}

